import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { User } from "./types";

export interface PresenceLocation {
  screen: "dashboard" | "lesson" | "login";
  lessonId?: string;
  step?: number;
}

export interface Peer {
  sessionId: string;
  user: { id: string; name: string; initials: string };
  location: PresenceLocation;
}

interface Ctx {
  peers: Peer[];
  selfSessionId: string | null;
  setLocation: (loc: PresenceLocation) => void;
}

const PresenceCtx = createContext<Ctx>({
  peers: [],
  selfSessionId: null,
  setLocation: () => {},
});

export function usePresence() {
  return useContext(PresenceCtx);
}

interface ProviderProps {
  user: User;
  children: ReactNode;
}

export function PresenceProvider({ user, children }: ProviderProps) {
  const [peers, setPeers] = useState<Peer[]>([]);
  const [selfSessionId, setSelfSessionId] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const lastLocRef = useRef<PresenceLocation>({ screen: "dashboard" });
  const reconnectRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let pingTimer: ReturnType<typeof setInterval> | null = null;

    function connect() {
      if (cancelled) return;
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const ws = new WebSocket(`${proto}://${window.location.host}/ws/presence`);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectRef.current = 0;
        ws.send(
          JSON.stringify({
            type: "hello",
            user: { id: user.id, name: user.name, initials: user.initials },
            location: lastLocRef.current,
          }),
        );
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 20_000);
      };

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === "welcome") setSelfSessionId(msg.sessionId);
          else if (msg.type === "presence") setPeers(msg.peers);
        } catch {
          // ignore
        }
      };

      ws.onclose = () => {
        if (pingTimer) clearInterval(pingTimer);
        pingTimer = null;
        if (cancelled) return;
        const delay = Math.min(10_000, 500 * 2 ** reconnectRef.current++);
        setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      cancelled = true;
      if (pingTimer) clearInterval(pingTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [user.id, user.name, user.initials]);

  const setLocation = useMemo(
    () => (loc: PresenceLocation) => {
      const prev = lastLocRef.current;
      if (
        prev.screen === loc.screen &&
        prev.lessonId === loc.lessonId &&
        prev.step === loc.step
      ) {
        return;
      }
      lastLocRef.current = loc;
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "move", location: loc }));
      }
    },
    [],
  );

  const value = useMemo<Ctx>(
    () => ({ peers, selfSessionId, setLocation }),
    [peers, selfSessionId, setLocation],
  );

  return <PresenceCtx.Provider value={value}>{children}</PresenceCtx.Provider>;
}

export function useOtherPeers(): Peer[] {
  const { peers, selfSessionId } = usePresence();
  return peers.filter((p) => p.sessionId !== selfSessionId);
}
